import { useEffect, useMemo, useRef, useState } from "react";

import Autocomplete from "@/components/Autocomplete";
import Chart from "@/components/Chart";
import DetailsCard from "@/components/DetailsCard";
import EmptyState from "@/components/EmptyState";
import SectionHeading from "@/components/SectionHeading";
import SelectableNavList from "@/components/SelectableNavList";
import { sysColors } from "@/assets/theme/colors";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import type { CareerPlanCompetencyScore } from "./careerPlanHistory";
import { getCareerPlanOnboardingComparisonRoleFit, sortCareerPlanOnboardingRoleTitles } from "./careerPlanOnboarding";
import type { CareerPlanRoleRubric } from "../queries";
import ExperienceIndexArchetypeSection from "./_ExperienceIndexArchetypeSection";
import ExperienceIndexCompetencyStandingRow from "./_ExperienceIndexCompetencyStandingRow";
import ExperienceIndexPriorityGapsSection from "./_ExperienceIndexPriorityGapsSection";
import ExperienceIndexTopCompatibleRolesSection from "./_ExperienceIndexTopCompatibleRolesSection";
import { matchExperienceArchetype } from "./experienceIndexArchetypes";
import { EXPERIENCE_INDEX_NAV_CATEGORIES, type ExperienceIndexCategoryId } from "./experienceIndexCategories";
import { EXPERIENCE_INDEX_CATEGORY_ICONS } from "./experienceIndexIcons";
import {
  RADAR_MINIMUM_COMPLETED_COMPETENCY_COUNT,
  canShowExperienceIndexRadar,
  getCareerRoleFits,
  getCompetencyStandingRows,
  getRadarComparisonData,
  type CareerRoleFit
} from "./experienceIndexResults";

const categoryNavOptions = EXPERIENCE_INDEX_NAV_CATEGORIES.map((category) => ({
  value: category.id,
  label: category.label,
  iconName: EXPERIENCE_INDEX_CATEGORY_ICONS[category.id]
}));

const SCORES_SERIES = [
  {
    dataKey: "member",
    name: "Scores",
    color: sysColors["sys-brand-primary"]
  }
];

const ROLE_RUBRIC_SERIES = [
  ...SCORES_SERIES,
  {
    dataKey: "desired",
    name: "Role rubric",
    color: sysColors["sys-brand-outline"],
    fillOpacity: 0.12
  }
];

const SCORES_HEADING = {
  heading: "Experience Index Scores",
  body: "Review completed competency scores by category. Select a comparison role to overlay the role rubric."
};

const ROLE_RUBRIC_HEADING = {
  heading: "Role Rubric Comparison",
  body: "Compare completed competency scores to the selected role's rubric. On larger screens, All includes a radar of every competency."
};

const SCORES_HEADING_WITHOUT_ROLES = "Review completed competency scores by category.";

export default function ExperienceIndexResultsTab({
  assessmentComplete = false,
  assessmentComparisonRole = null,
  scores,
  rubrics,
  neverStarted = false,
  onSelectCompetency,
  onStart
}: {
  assessmentComplete?: boolean;
  assessmentComparisonRole?: string | null;
  scores: CareerPlanCompetencyScore[];
  rubrics: CareerPlanRoleRubric[];
  neverStarted?: boolean;
  onSelectCompetency?: (competencyId: string) => void;
  onStart?: () => void;
}) {
  const { isCompactLayout } = useIsCompactLayout();
  const roleFits = useMemo(() => getCareerRoleFits(rubrics, scores), [rubrics, scores]);
  const storedComparisonRoleId = useMemo(
    () => getCareerPlanOnboardingComparisonRoleFit({ assessmentComparisonRole, scores }, rubrics)?.id ?? "",
    [assessmentComparisonRole, rubrics, scores]
  );
  const [comparisonRoleId, setComparisonRoleId] = useState(storedComparisonRoleId);
  const [categoryId, setCategoryId] = useState<ExperienceIndexCategoryId>("all");
  const comparisonRoleTouchedRef = useRef(false);
  const selectedRoleFit = roleFits.find((fit) => fit.id === comparisonRoleId) ?? null;
  const [query, setQuery] = useState(selectedRoleFit?.title ?? "");
  const comparisonRoleOptions = useMemo(
    () => [...roleFits].sort((left, right) => sortCareerPlanOnboardingRoleTitles(left.title, right.title)),
    [roleFits]
  );
  const filteredRoles = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return comparisonRoleOptions;
    return comparisonRoleOptions.filter((fit) => fit.title.toLowerCase().includes(normalizedQuery));
  }, [comparisonRoleOptions, query]);

  useEffect(() => {
    setComparisonRoleId((current) => {
      const currentStillValid = roleFits.some((fit) => fit.id === current);
      if (comparisonRoleTouchedRef.current) {
        return currentStillValid ? current : "";
      }
      return storedComparisonRoleId && roleFits.some((fit) => fit.id === storedComparisonRoleId)
        ? storedComparisonRoleId
        : "";
    });
  }, [roleFits, storedComparisonRoleId]);

  useEffect(() => {
    setQuery(selectedRoleFit?.title ?? "");
  }, [selectedRoleFit?.title]);

  const standingRows = useMemo(
    () => getCompetencyStandingRows(selectedRoleFit, scores, categoryId),
    [categoryId, scores, selectedRoleFit]
  );
  const radarData = useMemo(
    () => getRadarComparisonData(selectedRoleFit, scores, categoryId),
    [categoryId, scores, selectedRoleFit]
  );
  const showAllCompetenciesRadar =
    categoryId === "all" && !isCompactLayout && radarData.length >= RADAR_MINIMUM_COMPLETED_COMPETENCY_COUNT;
  const showRadar = showAllCompetenciesRadar || canShowExperienceIndexRadar(radarData, categoryId);
  const heading = selectedRoleFit
    ? ROLE_RUBRIC_HEADING
    : {
        heading: SCORES_HEADING.heading,
        body: roleFits.length > 0 ? SCORES_HEADING.body : SCORES_HEADING_WITHOUT_ROLES
      };
  const radarSeries = selectedRoleFit ? ROLE_RUBRIC_SERIES : SCORES_SERIES;
  const selectedCategoryLabel =
    EXPERIENCE_INDEX_NAV_CATEGORIES.find((category) => category.id === categoryId)?.label ?? categoryId;
  const archetype = useMemo(
    () => (assessmentComplete ? matchExperienceArchetype(scores) : null),
    [assessmentComplete, scores]
  );

  const handleSelectComparisonRole = (role: CareerRoleFit) => {
    comparisonRoleTouchedRef.current = true;
    setComparisonRoleId(role.id);
    setQuery(role.title);
  };

  const handleClearComparisonRole = () => {
    comparisonRoleTouchedRef.current = true;
    setComparisonRoleId("");
    setQuery("");
  };

  if (scores.length === 0) {
    return (
      <EmptyState
        title="No Experience Index Results"
        iconName="quiz"
        caption={
          neverStarted
            ? "This person has not answered Experience Index questions yet."
            : "No Experience Index results are available for this snapshot."
        }
        action={
          onStart
            ? {
                children: "Start Experience Index",
                icon: "add",
                onClick: onStart,
                testId: "career-plan-ei-results-start-empty"
              }
            : undefined
        }
      />
    );
  }

  const roleRubricHeading = <SectionHeading heading={heading.heading} body={heading.body} />;
  const roleRubricDetail = (
    <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
      <SelectableNavList
        className="w-full shrink-0 lg:w-80"
        label="Category to View"
        options={categoryNavOptions}
        value={categoryId}
        onChange={(value) => setCategoryId(value as ExperienceIndexCategoryId)}
        data-testid="career-plan-category-nav"
      />

      <div className="min-w-0 flex-1" data-testid="career-plan-role-rubric-detail">
        {standingRows.length === 0 ? (
          <EmptyState
            title="No Competencies in This Category"
            iconName="quiz"
            caption={`There are no Experience Index competencies for ${selectedCategoryLabel}.`}
          />
        ) : (
          <DetailsCard className="w-full" testId="career-plan-ei-scores-card">
            <div className="flex w-full flex-col gap-6">
              {showRadar ? (
                <div
                  className="w-full"
                  data-testid={showAllCompetenciesRadar ? "career-plan-all-competencies-radar" : undefined}
                >
                  <Chart
                    variant="radar"
                    bordered={false}
                    className="w-full"
                    data={radarData}
                    height={showAllCompetenciesRadar ? 400 : 320}
                    maxValue={5}
                    series={radarSeries}
                    outerRadius="72%"
                    margin={{ top: 28, right: 40, bottom: 12, left: 40 }}
                  />
                </div>
              ) : null}

              <div className="flex flex-col gap-3" data-testid="career-plan-competency-standing">
                {standingRows.map((row) => (
                  <ExperienceIndexCompetencyStandingRow key={row.id} row={row} onViewAnswers={onSelectCompetency} />
                ))}
              </div>
            </div>
          </DetailsCard>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col" data-testid="career-plan-ei-results-tab">
      {archetype ? (
        <ExperienceIndexArchetypeSection
          archetype={archetype}
          scores={scores}
          onSelectCompetency={onSelectCompetency}
        />
      ) : null}

      {roleFits.length > 0 ? (
        <div className="mt-12 flex flex-col gap-6">
          <div
            onBlur={() => {
              setQuery(selectedRoleFit?.title ?? "");
            }}
          >
            <Autocomplete
              data-testid="career-plan-comparison-role-select"
              variant="searchableDropdown"
              label="Comparison Role"
              query={query}
              data={filteredRoles}
              menuClassName="max-h-[20rem]"
              onChangeText={setQuery}
              onSelect={handleSelectComparisonRole}
              reset={handleClearComparisonRole}
              onMouseDown={(event: MouseEvent) => {
                event.preventDefault();
              }}
              renderItem={(role) => <div className="text-body-large">{role.title}</div>}
            />
          </div>

          {selectedRoleFit ? (
            <ExperienceIndexPriorityGapsSection
              roleFit={selectedRoleFit}
              scores={scores}
              onSelectCompetency={onSelectCompetency}
            />
          ) : null}

          <div className="[&>div:first-child]:pt-0">
            {roleRubricHeading}
            {roleRubricDetail}
          </div>
        </div>
      ) : (
        <div>
          {roleRubricHeading}
          {roleRubricDetail}
        </div>
      )}

      {assessmentComplete ? (
        <ExperienceIndexTopCompatibleRolesSection comparisonRoleId={comparisonRoleId} roleFits={roleFits} />
      ) : null}
    </div>
  );
}
